import { t } from "@alluzo/shared"
import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from "lucide-react"
import { createContext, useContext, useEffect, useSyncExternalStore, type CSSProperties, type ReactNode } from "react"
import { Toaster as Sonner, type ToasterProps } from "sonner"

import { cn } from "~/lib/utils"
import { useThemeStore } from "~/stores/theme-store"

/**
 * The kit's toast (web-app.md section 6; kit.css .toast, .toast-stack,
 * web-shell.css `.ws-main .toast`): inverted, the ink ground with light
 * words, 356 wide, 12 and 16 in, the card corner and the high shadow. The
 * kind is carried by the icon, on a tint of its own colour, and by the title:
 * success green, error red, warning amber (the other scheme's intense text,
 * `--in-inverse` and its kin). Its one act (Undo) is an outline button on the
 * ink. Hovered, a × at the top-right corner dismisses it. Five seconds, or as
 * long as the pointer rests on it. The drawing is in globals.css
 * (`.toaster`), over sonner's own styles, and so is its motion: it rises at
 * `travel` and fades in at `enter` on the `out` curve, and leaves at `leave`.
 *
 * One toaster per pane. The main box holds one (`PaneToaster`, drawn by
 * `Main`) and the right panel holds its own (`PANEL_TOASTER`, sonner's
 * toaster `id`, drawn by `Aside`), each centred in its pane, 24 from its foot
 * (24 above a fixed foot: `--pane-foot`). Several at once stack, newest in
 * front and at most three, and open into a column 8 apart on hover.
 *
 * `Toaster` is the window's toaster, for the pages outside the shell
 * (sign-in, setup): it steps aside while a main box's toaster is mounted, so
 * a toast is never drawn twice.
 */

/** The right panel's toaster: `toast(…, { toasterId: PANEL_TOASTER })`, or `usePaneToasterId()` inside the panel. */
export const PANEL_TOASTER = "panel"

const PaneId = createContext<string | undefined>(undefined)

/** Which toaster a toast raised here belongs to: the panel's inside the right panel, the main box's everywhere else. */
export function usePaneToasterId(): string | undefined {
  return useContext(PaneId)
}

/** What a pane's children read to send their toasts to the pane's own toaster. */
export function PaneToasterScope({ id, children }: { id: string; children: ReactNode }) {
  return <PaneId.Provider value={id}>{children}</PaneId.Provider>
}

/* How many main boxes hold a toaster now. The window's toaster draws only at 0. */
let mainToasters = 0
const listeners = new Set<() => void>()
const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
const mainToasterCount = (): number => mainToasters
const bump = (by: number): void => {
  mainToasters += by
  for (const listener of listeners) listener()
}

const ICON = "size-4"

function KitToaster({ className, style, ...props }: ToasterProps) {
  const scheme = useThemeStore((state) => state.scheme)
  return (
    <Sonner
      theme={scheme}
      className={cn("toaster group", className)}
      position="bottom-center"
      duration={5000}
      gap={8}
      visibleToasts={3}
      closeButton
      offset={{ bottom: "calc(24px + var(--pane-foot))" }}
      customAriaLabel={t("ui.toast.region")}
      icons={{
        success: <CircleCheckIcon className={ICON} />,
        info: <InfoIcon className={ICON} />,
        warning: <TriangleAlertIcon className={ICON} />,
        error: <OctagonXIcon className={ICON} />,
        loading: <Loader2Icon className={cn(ICON, "animate-spin")} />,
      }}
      toastOptions={{ closeButtonAriaLabel: t("common.dismiss") }}
      style={
        {
          "--normal-bg": "var(--foreground)",
          "--normal-text": "var(--background)",
          "--normal-border": "transparent",
          "--border-radius": "var(--radius-card)",
          "--width": "356px",
          ...style,
        } as CSSProperties
      }
      {...props}
    />
  )
}

/** The window's toaster, in the root layout: for the pages outside the shell. */
function Toaster(props: ToasterProps) {
  const mains = useSyncExternalStore(subscribe, mainToasterCount, mainToasterCount)
  if (mains > 0) return null
  return <KitToaster {...props} />
}

/**
 * A pane's toaster, drawn inside the pane's box (which is `relative`): the
 * main box's with no `id`, the right panel's with `PANEL_TOASTER`.
 */
function PaneToaster({ id }: { id?: string }) {
  useEffect(() => {
    if (id !== undefined) return undefined
    bump(1)
    return () => bump(-1)
  }, [id])
  return <KitToaster id={id} className="toaster-pane" />
}

export { Toaster, PaneToaster }
